import { useState } from 'react'

import { Box, Typography, Button } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import Swal from 'sweetalert2'

import { MODULE_NAME, PERMISSIONS } from '../../constants'
import { translate, useLanguage } from '../../i18n'
import { hasPermission } from '../../utils'
import { refreshCurrentUserMe } from '../../utils/permissions'

import { getRoleList, deleteRole as deleteRoleApi } from './apis'
import AddRoleModal from './components/AddRoleModal'
import RolesTab from './components/RolesTab'

// Default page size for the roles listing. Matches the convention used by
// UserTable / AccessPointsTable (10 rows per page) so the Roles tab looks
// and behaves the same as the other listing pages.
const DEFAULT_ROWS_PER_PAGE = 10

function normalizeRole(r) {
  const users = Array.isArray(r.users) ? r.users : []
  const branchUserCounts = Array.isArray(r.branchUserCounts) ? r.branchUserCounts : []
  // Prefer `totalUserCount` from /guardian-roles, then legacy aliases
  // (`totalUsers`, `usersCount`), and fall back to deriving from `users`
  // — which lets the same shape work for fixtures / older endpoints.
  const totalUsers =
    typeof r.totalUserCount === 'number'
      ? r.totalUserCount
      : typeof r.totalUsers === 'number'
        ? r.totalUsers
        : typeof r.usersCount === 'number'
          ? r.usersCount
          : users.length
  return {
    id: r.id ?? r.uuid ?? r.name,
    uuid: r.uuid ?? r.id,
    name: r.name,
    totalUsers,
    totalUserCount: totalUsers,
    branchUserCounts,
    permissions: Array.isArray(r.permissions) ? r.permissions : [],
    users: users.map((u) => ({
      name: u.name ?? u.fullName ?? '',
      color: u.color ?? '#1976d2',
    })),
  }
}

// Pull the rows + total out of whatever envelope the backend returned on
// this request. `/guardian-roles` is paginated and the documented contract
// is `{ data: { data: Role[], meta: { total, page, limit, totalPages } } }`.
// We still tolerate the older non-paginated shapes (array-only body,
// double-nested) so a backend regression doesn't crash the page — it just
// falls back to "0 of 0" in the footer. Mirrors `extractList` /
// `extractTotal` in userManagement/apis.js so the helpers stay aligned.
function extractRoleList(response) {
  const payload = response?.data ?? response
  let list = []
  if (Array.isArray(payload?.data?.data)) list = payload.data.data
  else if (Array.isArray(payload?.data)) list = payload.data
  else if (Array.isArray(payload)) list = payload
  return list
}

function extractRoleTotal(response) {
  const payload = response?.data ?? response
  if (typeof payload?.data?.meta?.total === 'number') return payload.data.meta.total
  if (typeof payload?.data?.total === 'number') return payload.data.total
  if (typeof payload?.meta?.total === 'number') return payload.meta.total
  return 0
}

function useRolesQuery({ page, rowsPerPage }) {
  // 1-indexed `page` for the backend; rowsPerPage maps to `limit`. Both
  // ride on the query key so the next page gets its own cached slice and
  // back/forward navigation doesn't flash the stale page.
  return useQuery({
    queryKey: ['guardian-roles', { page, rowsPerPage }],
    queryFn: async () => {
      const { data } = await getRoleList({ page, limit: rowsPerPage })
      const list = extractRoleList(data)
      const total = extractRoleTotal(data)
      return { roles: list.map(normalizeRole), total }
    },
    keepPreviousData: true,
    refetchOnMount: 'always',
  })
}

function UsersRoles() {
  // `useLanguage` subscribes this component to language changes — using
  // the standalone `translate()` helper for the rendered copy below
  // wouldn't re-render the JSX when the user switches languages (the
  // cache update happens, but no state change triggers a re-render of
  // this subtree). `translate()` is still used inside the Swal toast
  // handler below — that path runs outside React's render scope, where
  // the hook isn't applicable.
  const { t } = useLanguage()

  // Branch-scoped permission — hide the Create Role control when the
  // current user is not allowed to create roles.
  const canCreate = hasPermission(MODULE_NAME.ROLE, PERMISSIONS.CREATE)

  const [addRoleOpen, setAddRoleOpen] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [viewingRole, setViewingRole] = useState(null)

  // Pagination state owned here so the page-level UI (header, footer)
  // and the listing stay in sync. Held in 1-indexed form for the API
  // boundary (see `useRolesQuery`); we convert to/from 0-indexed when
  // talking to `<PaginationFooter>`, which still uses the rest of the
  // app's 0-indexed convention.
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)

  const rolesQuery = useRolesQuery({ page, rowsPerPage })
  const roles = rolesQuery.data?.roles || []
  const totalCount = rolesQuery.data?.total ?? 0
  const qc = useQueryClient()

  const refreshRoles = () => {
    qc.invalidateQueries({ queryKey: ['guardian-roles'] })
    qc.invalidateQueries({ queryKey: ['guardian-modules'] })
    qc.invalidateQueries({ queryKey: ['guardian-role'] })
    rolesQuery.refetch()
  }

  const handleRoleCreated = () => {
    refreshRoles()
  }

  const handleRoleUpdated = () => {
    refreshRoles()
    // The active user's role may have been the one just edited — refresh
    // /me so the sidebar reflects any permission changes without forcing
    // a re-login. `force: true` bypasses the focus TTL because we just
    // performed a mutation that explicitly needs fresh data.
    refreshCurrentUserMe({ force: true })
  }

  const handleEditRole = (role) => {
    setEditingRole(role)
    setViewingRole(null)
    setAddRoleOpen(true)
  }

  const handleViewRole = (role) => {
    setEditingRole(null)
    setViewingRole(role)
    setAddRoleOpen(true)
  }

  const swalDefaults = {
    confirmButtonColor: '#3B82F6',
    cancelButtonColor: '#151f3b',
    background: 'var(--card-bg, #ffffff)',
    color: 'var(--text-primary, #212529)',
    customClass: {
      confirmButton: 'swal-btn-primary',
      cancelButton: 'swal-btn-secondary',
    },
  }

  const handleDeleteRole = async (role) => {
    const result = await Swal.fire({
      ...swalDefaults,
      title: translate('Do you want to delete this role?'),
      text: translate('Role "{{name}}" will be removed.', { name: role.name }),
      icon: 'warning',
      iconColor: '#DC3545',
      showCancelButton: true,
      confirmButtonText: translate('OK'),
      cancelButtonText: translate('Cancel'),
    })
    if (!result.isConfirmed) return
    try {
      await deleteRoleApi(role.uuid)
      await Swal.fire({
        ...swalDefaults,
        title: translate('Deleted!'),
        text: translate('The role has been deleted.'),
        icon: 'success',
        iconColor: '#198754',
        confirmButtonText: translate('OK'),
      })
      refreshRoles()
      // If the deleted role was the active user's, their effective
      // permissions change. Refresh /me so the sidebar reflects the new
      // (likely-restricted) state without forcing the user to re-login.
      // `force: true` bypasses the focus TTL because we just performed a
      // mutation that explicitly needs fresh data.
      refreshCurrentUserMe({ force: true })
    } catch (err) {
      Swal.fire({
        ...swalDefaults,
        title: translate('Error'),
        text:
          err?.response?.data?.status?.message ||
          err?.message ||
          translate('Failed to delete role.'),
        icon: 'error',
        iconColor: '#DC3545',
        confirmButtonText: translate('OK'),
      })
    }
  }

  const handleCreateRole = () => {
    setEditingRole(null)
    setViewingRole(null)
    setAddRoleOpen(true)
  }

  const handleModalClose = () => {
    setAddRoleOpen(false)
    setEditingRole(null)
    setViewingRole(null)
  }

  // `<PaginationFooter>` uses 0-indexed pages for the rest of the app.
  // Convert at the boundary so the rest of UsersRoles keeps the
  // 1-indexed `page` it sends to the backend.
  const handlePageChange = (nextZeroIndexed) => setPage(nextZeroIndexed + 1)
  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next)
    // Switching page size usually shrinks/grows the visible page out
    // from under the user — drop back to the first page so they don't
    // land on a now-empty index.
    setPage(1)
  }

  return (
    <Box>
      <Box className="flex justify-between items-center flex-wrap gap-2 mb-3">
        <div>
          <Typography variant="h4" fontWeight={700}>
            {t('ROLE PERMISSION')}
          </Typography>
          <Typography fontSize={13} color="text.secondary" sx={{ mb: 3 }}>
            {t(
              'A role provides access to predefined menus and features — assign one to each user to control what they can see and do.'
            )}
          </Typography>
        </div>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            sx={{ textTransform: 'none' }}
            aria-label={t('Create Role')}
            onClick={handleCreateRole}
          >
            {t('Create Role')}
          </Button>
        )}
      </Box>

      <RolesTab
        roles={roles}
        onEdit={handleEditRole}
        onDelete={handleDeleteRole}
        onView={handleViewRole}
        isLoading={rolesQuery.isLoading}
        isError={rolesQuery.isError}
        errorMessage={
          rolesQuery.error?.response?.data?.message ||
          rolesQuery.error?.message ||
          t('Failed to load roles. Please try again.')
        }
        onRetry={() => rolesQuery.refetch()}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <AddRoleModal
        open={addRoleOpen}
        onClose={handleModalClose}
        onCreated={handleRoleCreated}
        onUpdated={handleRoleUpdated}
        role={editingRole || viewingRole}
        viewOnly={Boolean(viewingRole)}
      />
    </Box>
  )
}

export default UsersRoles
